import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import GiftCardLink from '~/components/Links/GiftCardLink';
import NewTabLink from '~/components/Links/NewTabLink';
import RegisterLink from '~/components/Links/RegisterLink';
import {
  FT_GIFT_CARD_PURCHASE_ENABLED,
  FT_GIFT_CARD_PURCHASE_UNAVAILABLE_WARNING,
} from '../../constants/featureToggles';

const sections = [
  {
    title: 'How do I buy a SidelineSwap gift card?',
    body: (
      <>
        {FT_GIFT_CARD_PURCHASE_UNAVAILABLE_WARNING && (
          <p>* Gift card purchases are currently unavailable</p>
        )}
        {FT_GIFT_CARD_PURCHASE_ENABLED && (
          <>
            <p>
              Merely select a gift card amount and then submit a payment. The
              gift card will be delivered to you via email at which point it can
              be shared with anyone. Please note that the credit can only used
              by the account that account that claims the gift card.
            </p>
            <p>
              Click{' '}
              <GiftCardLink className="text-blue-500" page="purchase">
                here
              </GiftCardLink>{' '}
              to purchase a SidelineSwap gift card.
            </p>
          </>
        )}
      </>
    ),
  },
  {
    title: 'How do I redeem a gift card?',
    body: (
      <>
        <p>
          Instructions and a redemption code for your gift card will be sent to
          you via email. Gift cards can be activated by visiting{' '}
          <GiftCardLink className="text-blue-500">
            sidelineswap.com/gift-cards
          </GiftCardLink>
          . Once redeemed, gift card credits never expire.
        </p>
        <p>
          The amount of the gift card will be immediately added to your account
          balance and made available for use after it has been redeemed. The
          associated credit cannot be transferred to a different account once
          redeemed. For purchases exceeding the amount of credit associated with
          a gift card, the remaining balance must be paid via credit card or
          PayPal.
        </p>
        <p>
          Click{' '}
          <NewTabLink
            href="https://help.sidelineswap.com/hc/en-us/articles/360013147939-How-to-buy-with-site-credit"
            className="text-blue-500"
          >
            here
          </NewTabLink>{' '}
          to learn how to apply a credit to your SidelineSwap purchase.
        </p>
      </>
    ),
  },
  {
    title: 'Do I need an account to purchase a gift card?',
    body: (
      <p>
        Redemption of a SidelineSwap gift card will require you to be a
        registered SidelineSwap user. Create a SidelineSwap account{' '}
        <RegisterLink className="text-blue-500">here</RegisterLink>.
      </p>
    ),
  },
  {
    title: 'What happens if I lose my gift card?',
    body: (
      <p>
        If you have lost the gift card code, please{' '}
        <NewTabLink
          href="https://help.sidelineswap.com/hc/en-us/requests/new?ticket_form_id=360000525400"
          className="text-blue-500"
        >
          submit a request
        </NewTabLink>{' '}
        to our support team. Please provide the email address that the gift card
        code was emailed to.
      </p>
    ),
  },
  {
    title: 'Do gift cards expire?',
    body: (
      <p>
        SidelineSwap Gift Cards do not expire! You may use the credit from your
        gift card for any SidelineSwap purchase.
      </p>
    ),
  },
];

const Item = ({
  body,
  title,
}: {
  body: React.ReactNode;
  title: React.ReactNode;
}) => {
  const [opened, setOpened] = useState(false);

  return (
    // @ts-expect-error
    <motion.div
      layout
      className="overflow-hidden"
      animate={opened ? 'visible' : 'hidden'}
      transition={{ bounce: 0 }}
      variants={{
        hidden: {
          height: 24,
        },
        visible: {
          height: 'auto',
        },
      }}
    >
      <button
        onClick={() => setOpened(!opened)}
        className="text-grey-800 w-full text-left text-base font-bold md:w-auto"
      >
        {title}
      </button>

      {/* @ts-ignore */}
      <AnimatePresence>
        {opened && (
          // @ts-expect-error
          <motion.div
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="prose prose-blue my-2 leading-normal"
          >
            {body}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

interface Props {}

const GiftCardFaqSection: React.FC<Props> = () => {
  return (
    <>
      <h2 className="mb-6 text-center">FAQ</h2>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {sections.map(s => (
          <Item key={s.title} body={s.body} title={s.title} />
        ))}
      </div>
    </>
  );
};

export default GiftCardFaqSection;
